import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Button, message, Typography } from 'antd';
import { PaymentRequiredResultError } from '@lib/domain-common';
import { RightOutlined, PlusOutlined } from '@ant-design/icons';
import { getUseCase, RequestStatus } from '@lib/plugin-redux-core';
import { routeManager } from '@lib/route-manager';
import { useTranslation } from 'next-i18next';
import { GET_MESSAGES_USE_CASE, Message, MessageRole, MessageContent, MessageTimestamp, MessageStatus } from '../../../ai';
import { UniqueEntityID } from '@digibricks/core';
import { ConversationContainer } from './conversation';
import { MessageContainer } from './message';
import { AiProcessingPlaceholder } from './ai-processing-placeholder';
import { MentionListContainer } from './mention-list';
import { PromptInputContainer } from './prompt-input';
import { IsolatedChatLayout, SuggestionItemModal } from '../components';
import { APPEND_AI_MESSAGE_ON_STREAM_DONE, ADD_STOPPED_MESSAGE, REMOVE_ATTACHMENT, CLEAR_ATTACHMENTS, GET_AI_CHAT_USAGE_WALLET_USE_CASE, GET_AI_CHAT_ACCOUNTING_SUBSCRIPTION_USE_CASE } from '../redux/ai-chat/ai-chat.action';
import { selectGetMessagesStatus, selectMessagesNextToken, selectAttachments, selectUsageWallet, selectUsageWalletStatus, selectAccountingSubscription, selectAccountingSubscriptionStatus, selectSendMessageStatus } from '../redux/ai-chat/ai-chat.selectors';
import { useConversationMessages, useUploadConversationFiles, useStopMessage } from '../hooks';
import Image from 'next/image';
import { AttachmentListContainer } from './attachment-list';
import { FileSelector, isFileExtensionAllowed, isFileSizeWithinLimit } from '../../../file-management-app';
import { parseStructuredStreamPayload } from './ai-chat-stream.util';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
var Text = Typography.Text;
var AI_ERROR_MESSAGES = {
  INSUFFICIENT_BALANCE: 'You do not have enough AI tokens to continue. Please top up your balance and try again.',
  LLM_RATE_LIMITED: 'AI is busy right now. Please try again shortly.',
  LLM_INPUT_TOO_LARGE: 'Your input, files, or context are too large. Reduce them before trying again.',
  LLM_QUOTA_EXCEEDED: 'The AI service is temporarily out of quota.',
  LLM_REQUEST_FAILED: 'The AI request failed. Please try again.'
};
var AIChatWrapper = styled.div.withConfig({
  displayName: "ai-chatcontainer__AIChatWrapper",
  componentId: "sc-a1bapt-0"
})(["display:flex;flex-direction:column;height:100%;width:100%;background-color:#fff;overflow:hidden;"]);
var ChatHeader = styled.div.withConfig({
  displayName: "ai-chatcontainer__ChatHeader",
  componentId: "sc-a1bapt-1"
})(["display:flex;align-items:center;justify-content:space-between;padding:14px 16px;border-bottom:1px solid #f0f0f0;background-color:#fff;flex-shrink:0;"]);
var HeaderLeft = styled.div.withConfig({
  displayName: "ai-chatcontainer__HeaderLeft",
  componentId: "sc-a1bapt-2"
})(["display:flex;align-items:center;gap:10px;"]);
var HeaderIcon = styled.div.withConfig({
  displayName: "ai-chatcontainer__HeaderIcon",
  componentId: "sc-a1bapt-3"
})(["width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;"]);
var HeaderTitle = styled(Text).withConfig({
  displayName: "ai-chatcontainer__HeaderTitle",
  componentId: "sc-a1bapt-4"
})(["font-size:15px;font-weight:600;color:#333;"]);
var CollapseButton = styled.button.withConfig({
  displayName: "ai-chatcontainer__CollapseButton",
  componentId: "sc-a1bapt-5"
})(["width:28px;height:28px;padding:0;display:flex;align-items:center;justify-content:center;border:none;background:transparent;color:#999;cursor:pointer;&:hover{color:#1890ff;background:#f5f5f5;}"]);
var HeaderRight = styled.div.withConfig({
  displayName: "ai-chatcontainer__HeaderRight",
  componentId: "sc-a1bapt-6"
})(["display:flex;align-items:center;gap:4px;"]);
var ChatContent = styled.div.withConfig({
  displayName: "ai-chatcontainer__ChatContent",
  componentId: "sc-a1bapt-7"
})(["flex:1;display:flex;flex-direction:column;min-height:0;overflow:hidden;"]);
var ChatPanel = styled.div.withConfig({
  displayName: "ai-chatcontainer__ChatPanel",
  componentId: "sc-a1bapt-8"
})(["flex:1;display:flex;flex-direction:column;min-height:0;"]);
var ErrorBanner = styled.div.withConfig({
  displayName: "ai-chatcontainer__ErrorBanner",
  componentId: "sc-a1bapt-9"
})(["padding:8px 16px;background:#fff2f0;color:#ff4d4f;font-size:13px;"]);
var UNKNOWN_REFRESH_TIME = 'the next refresh time';
var FREE_PLAN_API_ID = '8d83edb5-4443-48de-8e8d-dd957a254722';

var isPaymentRequiredError = function isPaymentRequiredError(error) {
  var _candidate$error, _candidate$constructo;

  if (!error) return false;
  if (error instanceof PaymentRequiredResultError) return true;
  var candidate = error;
  var errorMessage = typeof candidate.error === 'string' ? candidate.error : ((_candidate$error = candidate.error) === null || _candidate$error === void 0 ? void 0 : _candidate$error.message) || candidate.message || '';
  return ((_candidate$constructo = candidate.constructor) === null || _candidate$constructo === void 0 ? void 0 : _candidate$constructo.name) === 'PaymentRequiredResultError' || candidate.status === 402 || candidate.statusCode === 402 || /payment|required|token|credit/i.test(errorMessage);
};

var formatRefreshTime = function formatRefreshTime(value) {
  if (!value) return UNKNOWN_REFRESH_TIME;
  var timestamp = typeof value === 'number' || /^\d+$/.test(String(value)) ? Number(value) * (Number(value) < 10000000000 ? 1000 : 1) : Date.parse(String(value));

  if (!Number.isFinite(timestamp)) {
    return String(value);
  }

  return new Date(timestamp).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

var buildPlanUrl = function buildPlanUrl() {
  var planPath = routeManager.standard.identity.fullPath.accountSetting.plan.path;
  if (false) return planPath;
  return new URL(planPath, window.location.origin).toString();
};

var buildTokenExpiredMessage = function buildTokenExpiredMessage(isFreePlan, refreshTime, translate) {
  var baseMessage = translate('error.tokenExpired', {
    refreshTime: refreshTime,
    defaultValue: "You've used all your AI tokens. Please wait until {{refreshTime}} for your next refresh."
  });

  if (!isFreePlan) {
    return baseMessage;
  }

  return translate('error.tokenExpiredWithUpgrade', {
    refreshTime: refreshTime,
    upgradeUrl: buildPlanUrl(),
    defaultValue: "You've used all your AI tokens. Please wait until {{refreshTime}} for your next refresh. Or upgrade your plan here: {{upgradeUrl}}"
  });
};

export var AIChatContainer = function AIChatContainer(_ref) {
  var _activeModelRules$all;

  var resourceId = _ref.resourceId,
      _ref$resourceType = _ref.resourceType,
      resourceType = _ref$resourceType === void 0 ? 'form' : _ref$resourceType,
      _ref$showHeader = _ref.showHeader,
      showHeader = _ref$showHeader === void 0 ? true : _ref$showHeader,
      onCollapse = _ref.onCollapse,
      _onStreamDone = _ref.onStreamDone,
      _onComplete = _ref.onComplete,
      onStreamChunk = _ref.onStreamChunk,
      slots = _ref.slots,
      onBuildingDataBeforeSendMessage = _ref.onBuildingDataBeforeSendMessage,
      onRemoveMention = _ref.onRemoveMention,
      onMentionClick = _ref.onMentionClick,
      mentions = _ref.mentions,
      onRemoveAttachment = _ref.onRemoveAttachment,
      _onProcessingChange = _ref.onProcessingChange,
      onSnapshotMentionResources = _ref.onSnapshotMentionResources,
      onStopRequested = _ref.onStopRequested,
      icons = _ref.icons,
      walletPercent = _ref.walletPercent,
      walletLoading = _ref.walletLoading,
      onAfterStreamEnd = _ref.onAfterStreamEnd,
      formId = _ref.formId,
      extraOptions = _ref.extraOptions,
      extraFiles = _ref.extraFiles,
      onExtraFilesChange = _ref.onExtraFilesChange;

  var _useTranslation = useTranslation('ai-chat'),
      t = _useTranslation.t;

  var _useConversationMessa = useConversationMessages({
    resourceId: resourceId,
    resourceType: resourceType
  }),
      messages = _useConversationMessa.messages,
      currentConversationId = _useConversationMessa.currentConversationId,
      isLoadingGetConversationsWithMessages = _useConversationMessa.isLoadingGetConversationsWithMessages,
      error = _useConversationMessa.error,
      createNewConversation = _useConversationMessa.createNewConversation;

  var dispatch = useDispatch();
  var messagesNextToken = useSelector(selectMessagesNextToken);
  var getMessagesStatus = useSelector(selectGetMessagesStatus);
  var attachments = useSelector(selectAttachments);
  var usageWalletStatus = useSelector(selectUsageWalletStatus);
  var usageWallet = useSelector(selectUsageWallet);
  var accountingSubscriptionStatus = useSelector(selectAccountingSubscriptionStatus);
  var accountingSubscription = useSelector(selectAccountingSubscription);
  var sendMessageStatus = useSelector(selectSendMessageStatus);
  var getMessagesUseCase = getUseCase(GET_MESSAGES_USE_CASE, dispatch);
  var getUsageWalletUseCase = getUseCase(GET_AI_CHAT_USAGE_WALLET_USE_CASE, dispatch);
  var getAccountingSubscriptionUseCase = getUseCase(GET_AI_CHAT_ACCOUNTING_SUBSCRIPTION_USE_CASE, dispatch);
  var removeAttachmentUseCase = getUseCase(REMOVE_ATTACHMENT, dispatch);
  var onLoadMore = useCallback(function () {
    if (!currentConversationId || !messagesNextToken || getMessagesStatus === RequestStatus.EXECUTE) {
      return;
    }

    getMessagesUseCase.execute({
      conversationId: currentConversationId,
      nextToken: messagesNextToken
    });
  }, [currentConversationId, messagesNextToken, getMessagesStatus, getMessagesUseCase]);

  var _useState = useState(false),
      isTyping = _useState[0],
      setIsTyping = _useState[1];

  var _useState2 = useState(''),
      streamingContent = _useState2[0],
      setStreamingContent = _useState2[1];

  var _useState3 = useState(null),
      localError = _useState3[0],
      setLocalError = _useState3[1];

  var _useState4 = useState(null),
      lastCompletedMessageId = _useState4[0],
      setLastCompletedMessageId = _useState4[1];

  var _useState5 = useState(false),
      isProcessing = _useState5[0],
      setIsProcessing = _useState5[1];

  var _useState6 = useState(false),
      isApplyingChanges = _useState6[0],
      setIsApplyingChanges = _useState6[1];

  var _useState7 = useState(false),
      isProcessStopped = _useState7[0],
      setIsProcessStopped = _useState7[1];

  var _useState8 = useState(false),
      isPendingPrompt = _useState8[0],
      setIsPendingPrompt = _useState8[1];

  var _useState9 = useState(false),
      isOpenModal = _useState9[0],
      setIsOpenModal = _useState9[1];

  var _useState10 = useState(null),
      itemSelected = _useState10[0],
      setItemSelected = _useState10[1];

  var _useState11 = useState(null),
      activeModelRules = _useState11[0],
      setActiveModelRules = _useState11[1];

  var _useState12 = useState(false),
      retryAvailable = _useState12[0],
      setRetryAvailable = _useState12[1];

  var stopRequestedRef = useRef(false);
  var tokenExpiredMessageStateRef = useRef('idle');
  var hasSeenTokenExpiredBillingRequestRef = useRef(false);
  var pendingPromptTimeoutRef = useRef(null);
  useEffect(function () {
    getAccountingSubscriptionUseCase.execute(null); // The use-case handle is created by the DI helper; fetch the current plan once per chat mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var invalidAttachments = useMemo(function () {
    return !activeModelRules ? [] : (attachments !== null && attachments !== void 0 ? attachments : []).filter(function (attachment) {
      return !isFileExtensionAllowed(attachment.name, activeModelRules.allowedFileExtensions) || activeModelRules.fileSizeLimit <= 0 || !isFileSizeWithinLimit(attachment.size, activeModelRules.fileSizeLimit);
    });
  }, [activeModelRules, attachments]);
  useEffect(function () {
    if (invalidAttachments.length === 0) return;
    invalidAttachments.forEach(function (attachment) {
      return removeAttachmentUseCase.execute(attachment.id);
    });
    message.info("Removed ".concat(invalidAttachments.length, " attachment").concat(invalidAttachments.length === 1 ? '' : 's', " not supported by the selected model."));
  }, [invalidAttachments, removeAttachmentUseCase]);
  var clearPendingPromptTimeout = useCallback(function () {
    if (pendingPromptTimeoutRef.current) {
      clearTimeout(pendingPromptTimeoutRef.current);
      pendingPromptTimeoutRef.current = null;
    }
  }, []);
  var parsedStreamingContent = useMemo(function () {
    return parseStructuredStreamPayload(streamingContent);
  }, [streamingContent]);
  var hasReceivedStreamChunk = streamingContent.length > 0;
  var streamingDataContent = parsedStreamingContent.data;
  var isStreamingData = !isProcessStopped && hasReceivedStreamChunk && (parsedStreamingContent.activeField === 'data' || parsedStreamingContent.hasDetectedDataField && (isTyping || isProcessing || isApplyingChanges)); // Add a ref for the prompt input

  var promptInputRef = useRef(null);

  var _useStopMessage = useStopMessage({
    conversationId: currentConversationId,
    onProcessingChange: function onProcessingChange(processing) {
      setIsProcessing(processing);
      _onProcessingChange === null || _onProcessingChange === void 0 ? void 0 : _onProcessingChange(processing);
    },
    onStopRequested: onStopRequested
  }),
      isStoppedByUser = _useStopMessage.isStoppedByUser,
      handleUserStopRequest = _useStopMessage.handleUserStopRequest;

  React.useEffect(function () {
    if (!isPaymentRequiredError(error)) {
      if (tokenExpiredMessageStateRef.current === 'appended') {
        tokenExpiredMessageStateRef.current = 'idle';
        hasSeenTokenExpiredBillingRequestRef.current = false;
      }

      return;
    }

    if (tokenExpiredMessageStateRef.current !== 'idle') {
      return;
    }

    tokenExpiredMessageStateRef.current = 'requested';
    hasSeenTokenExpiredBillingRequestRef.current = false;
    getUsageWalletUseCase.execute(null);
    getAccountingSubscriptionUseCase.execute(null);
  }, [error, getAccountingSubscriptionUseCase, getUsageWalletUseCase]);
  React.useEffect(function () {
    if (sendMessageStatus !== RequestStatus.ERROR) {
      return;
    }

    clearPendingPromptTimeout();
    setIsPendingPrompt(false);
    setIsTyping(false);
    setIsProcessing(false);
    setIsApplyingChanges(false);
    _onProcessingChange === null || _onProcessingChange === void 0 ? void 0 : _onProcessingChange(false);
  }, [clearPendingPromptTimeout, _onProcessingChange, sendMessageStatus]);
  React.useEffect(function () {
    if (tokenExpiredMessageStateRef.current === 'requested' && (usageWalletStatus === RequestStatus.EXECUTE || accountingSubscriptionStatus === RequestStatus.EXECUTE)) {
      tokenExpiredMessageStateRef.current = 'loading';
      hasSeenTokenExpiredBillingRequestRef.current = true;
      return;
    }

    if (tokenExpiredMessageStateRef.current !== 'loading') {
      return;
    }

    if (!hasSeenTokenExpiredBillingRequestRef.current) {
      return;
    }

    var isWalletRequestDone = usageWalletStatus === RequestStatus.SUCCESS || usageWalletStatus === RequestStatus.ERROR;
    var isSubscriptionRequestDone = accountingSubscriptionStatus === RequestStatus.SUCCESS || accountingSubscriptionStatus === RequestStatus.ERROR;

    if (!isWalletRequestDone || !isSubscriptionRequestDone) {
      return;
    }

    tokenExpiredMessageStateRef.current = 'appended';
    var tokenExpiredMessage = Message.create({
      id: new UniqueEntityID().toString(),
      status: MessageStatus.Done,
      conversationId: currentConversationId !== null && currentConversationId !== void 0 ? currentConversationId : "token-expired-".concat(resourceId),
      role: MessageRole.AIAssistant,
      content: MessageContent.create(buildTokenExpiredMessage((accountingSubscription === null || accountingSubscription === void 0 ? void 0 : accountingSubscription.planId) === FREE_PLAN_API_ID, formatRefreshTime(usageWallet === null || usageWallet === void 0 ? void 0 : usageWallet.nextRefreshTime), t)).getValue(),
      createdTime: MessageTimestamp.now()
    }).getValue();
    dispatch({
      type: APPEND_AI_MESSAGE_ON_STREAM_DONE,
      payload: tokenExpiredMessage
    });
  }, [accountingSubscription === null || accountingSubscription === void 0 ? void 0 : accountingSubscription.planId, accountingSubscriptionStatus, currentConversationId, dispatch, resourceId, t, usageWallet === null || usageWallet === void 0 ? void 0 : usageWallet.nextRefreshTime, usageWalletStatus]);

  var _useUploadConversatio = useUploadConversationFiles(),
      fileSelectorOpen = _useUploadConversatio.fileSelectorOpen,
      fileSelectorTab = _useUploadConversatio.fileSelectorTab,
      isSaving = _useUploadConversatio.isSaving,
      handleOpenUploadModal = _useUploadConversatio.handleOpenUploadModal,
      handleSave = _useUploadConversatio.handleSave,
      setFileSelectorOpen = _useUploadConversatio.setFileSelectorOpen; // Cleanup on unmount


  React.useEffect(function () {
    return function () {
      clearPendingPromptTimeout();
    };
  }, [clearPendingPromptTimeout]);
  var handleStopConfirm = useCallback(function () {
    clearPendingPromptTimeout();
    stopRequestedRef.current = true;
    setIsProcessStopped(true);
    setStreamingContent('');
    setIsTyping(false);
    setIsProcessing(false);
    setIsApplyingChanges(false);
    setIsPendingPrompt(false);
    _onProcessingChange === null || _onProcessingChange === void 0 ? void 0 : _onProcessingChange(false); // Create and dispatch stopped message

    if (currentConversationId) {
      var stoppedMessage = Message.create({
        content: MessageContent.create('Stopped').getValue(),
        role: MessageRole.AIAssistant,
        conversationId: currentConversationId,
        id: new UniqueEntityID().toString(),
        createdTime: MessageTimestamp.now(),
        status: MessageStatus.Stopped
      }).getValue();
      dispatch({
        type: ADD_STOPPED_MESSAGE,
        payload: stoppedMessage
      });
    }
  }, [currentConversationId, dispatch]);
  var handleSuggestionClick = useCallback(function (item) {
    setIsOpenModal(true);
    setItemSelected(item);
  }, []);
  var handleCloseModal = useCallback(function () {
    setIsOpenModal(false);
    setItemSelected(null);
  }, []);
  var handleSubmitModal = useCallback(function (value) {
    var _promptInputRef$curre;

    setIsOpenModal(false);
    setItemSelected(null);
    (_promptInputRef$curre = promptInputRef.current) === null || _promptInputRef$curre === void 0 ? void 0 : _promptInputRef$curre.setInitialValue(value);
  }, []);
  var handleBeforeSendMessage = useCallback(function () {
    stopRequestedRef.current = false;
    setIsProcessStopped(false);
    setStreamingContent('');
    setIsTyping(false);
    setLocalError(null);
    setRetryAvailable(false);
    setIsProcessing(false);
    setIsApplyingChanges(false);
    clearPendingPromptTimeout(); // Delay the appearance of the processing placeholder by 1 second

    pendingPromptTimeoutRef.current = setTimeout(function () {
      setIsPendingPrompt(true);
    }, 1000);
  }, [clearPendingPromptTimeout]);
  var streamCallbacks = useMemo(function () {
    return {
      onChunk: function onChunk(chunk) {
        clearPendingPromptTimeout();

        if (stopRequestedRef.current) {
          return;
        }

        setIsPendingPrompt(false);
        setStreamingContent(function (prev) {
          return prev + chunk;
        });
        onStreamChunk === null || onStreamChunk === void 0 ? void 0 : onStreamChunk(chunk);
        setIsProcessing(function (prev) {
          if (!prev) {
            _onProcessingChange === null || _onProcessingChange === void 0 ? void 0 : _onProcessingChange(true);
          }

          return true;
        });
      },
      onStreamDone: function onStreamDone(metadata) {
        clearPendingPromptTimeout();

        if (stopRequestedRef.current) {
          return;
        }

        _onStreamDone === null || _onStreamDone === void 0 ? void 0 : _onStreamDone(metadata);
        setIsProcessing(false);
        _onProcessingChange === null || _onProcessingChange === void 0 ? void 0 : _onProcessingChange(false);
      },
      onComplete: function onComplete(message) {
        clearPendingPromptTimeout();

        if (stopRequestedRef.current) {
          return;
        }

        setIsApplyingChanges(true);
        setIsPendingPrompt(false); // Wait 3 seconds before clearing streaming content and appending message

        setTimeout(function () {
          dispatch({
            type: APPEND_AI_MESSAGE_ON_STREAM_DONE,
            payload: message
          });
          setLastCompletedMessageId(message.id.toString());
          setStreamingContent('');
          setIsTyping(false);
          setLocalError(null);
          setRetryAvailable(false);
          setIsApplyingChanges(false);
          setIsProcessing(false);
          _onComplete === null || _onComplete === void 0 ? void 0 : _onComplete(message);
          _onProcessingChange === null || _onProcessingChange === void 0 ? void 0 : _onProcessingChange(false); // Don't reset stopped state here - let the auto-hide timer handle it
        }, 500);
      },
      onError: function onError(err) {
        var _AI_ERROR_MESSAGES$co;

        clearPendingPromptTimeout();

        if (stopRequestedRef.current) {
          return;
        }

        var code = err === null || err === void 0 ? void 0 : err.code;
        var errorMessage = code ? (_AI_ERROR_MESSAGES$co = AI_ERROR_MESSAGES[code]) !== null && _AI_ERROR_MESSAGES$co !== void 0 ? _AI_ERROR_MESSAGES$co : 'The AI request failed.' : 'The AI request failed.';
        var conversationErrorMessage = Message.create({
          id: new UniqueEntityID().toString(),
          status: MessageStatus.Done,
          conversationId: currentConversationId !== null && currentConversationId !== void 0 ? currentConversationId : "ai-error-".concat(resourceId),
          role: MessageRole.AIAssistant,
          content: MessageContent.create(errorMessage).getValue(),
          createdTime: MessageTimestamp.now()
        }).getValue();
        dispatch({
          type: APPEND_AI_MESSAGE_ON_STREAM_DONE,
          payload: conversationErrorMessage
        });
        setLocalError(null);
        setRetryAvailable(code === 'LLM_RATE_LIMITED' || code === 'LLM_REQUEST_FAILED');
        setStreamingContent('');
        setIsTyping(false);
        setIsProcessing(false);
        setIsPendingPrompt(false);
        _onProcessingChange === null || _onProcessingChange === void 0 ? void 0 : _onProcessingChange(false);
      },
      onTypingStart: function onTypingStart() {
        clearPendingPromptTimeout();

        if (stopRequestedRef.current) {
          return;
        }

        setIsTyping(true);
        setIsPendingPrompt(false);
      },
      onTypingEnd: function onTypingEnd() {
        if (stopRequestedRef.current) {
          return;
        }

        setIsTyping(false);
      }
    };
  }, [currentConversationId, _onStreamDone, onStreamChunk, dispatch, _onComplete, _onProcessingChange, resourceId]);
  var isStreaming = isPendingPrompt || isTyping || hasReceivedStreamChunk || isApplyingChanges;
  return /*#__PURE__*/_jsxs(AIChatWrapper, {
    children: [showHeader && /*#__PURE__*/_jsxs(ChatHeader, {
      children: [/*#__PURE__*/_jsxs(HeaderLeft, {
        children: [/*#__PURE__*/_jsx(HeaderIcon, {
          children: /*#__PURE__*/_jsx(Image, {
            src: "/icons/ai-symbol.svg",
            alt: "AI Symbol",
            width: 24,
            height: 24
          })
        }), /*#__PURE__*/_jsx(HeaderTitle, {
          children: "AI Assistant"
        })]
      }), /*#__PURE__*/_jsxs(HeaderRight, {
        children: [/*#__PURE__*/_jsx(CollapseButton, {
          onClick: function onClick() {
            // Reset local UI state before creating new conversation
            stopRequestedRef.current = true;
            setStreamingContent('');
            setIsTyping(false);
            setIsProcessing(false);
            setIsApplyingChanges(false);
            setIsProcessStopped(false);
            setIsPendingPrompt(false);
            setLocalError(null);
            setLastCompletedMessageId(null);
            _onProcessingChange === null || _onProcessingChange === void 0 ? void 0 : _onProcessingChange(false);
            createNewConversation();
            dispatch({
              type: CLEAR_ATTACHMENTS,
              payload: null
            });
            onExtraFilesChange === null || onExtraFilesChange === void 0 ? void 0 : onExtraFilesChange([]);
          },
          type: "button",
          title: "New Conversation",
          children: /*#__PURE__*/_jsx(PlusOutlined, {})
        }), onCollapse && /*#__PURE__*/_jsx(CollapseButton, {
          onClick: onCollapse,
          type: "button",
          children: /*#__PURE__*/_jsx(RightOutlined, {})
        })]
      })]
    }), /*#__PURE__*/_jsx(ChatContent, {
      children: /*#__PURE__*/_jsx(IsolatedChatLayout, {
        messages: /*#__PURE__*/_jsxs(ConversationContainer, {
          formId: formId,
          messagesCount: messages.length + (isStreaming ? 1 : 0),
          isStreaming: isStreaming,
          isConversationLoading: isLoadingGetConversationsWithMessages,
          isLoadingMore: getMessagesStatus === RequestStatus.EXECUTE,
          onLoadMore: onLoadMore,
          emptyMessage: "No messages yet. Start the conversation.",
          onSuggestionClick: handleSuggestionClick,
          streamingContentSignal: streamingContent.length,
          children: [messages.map(function (msg) {
            return /*#__PURE__*/_jsx(MessageContainer, {
              message: msg,
              animateReveal: lastCompletedMessageId === msg.id.toString(),
              onRevealComplete: function onRevealComplete() {
                return setLastCompletedMessageId(null);
              }
            }, msg.id.toString());
          }), isStreaming && !isProcessStopped && /*#__PURE__*/_jsx(AiProcessingPlaceholder, {
            isTyping: isPendingPrompt || isTyping,
            isProcessing: isProcessing,
            isStoppedByUser: isStoppedByUser || isProcessStopped,
            isComplete: false,
            stopMessage: "Process Stopped",
            streamDataContent: streamingDataContent,
            isDataStreaming: isStreamingData
          })]
        }),
        input: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(AttachmentListContainer, {
            attachments: attachments,
            extraFiles: extraFiles,
            onRemoveInternal: function onRemoveInternal(id) {
              removeAttachmentUseCase.execute(id);
              onRemoveAttachment === null || onRemoveAttachment === void 0 ? void 0 : onRemoveAttachment(id);
            },
            onRemoveExtra: function onRemoveExtra(id) {
              var _extraFiles$filter;

              onExtraFilesChange === null || onExtraFilesChange === void 0 ? void 0 : onExtraFilesChange((_extraFiles$filter = extraFiles === null || extraFiles === void 0 ? void 0 : extraFiles.filter(function (f) {
                return f.id !== id;
              })) !== null && _extraFiles$filter !== void 0 ? _extraFiles$filter : []);
            }
          }), localError && /*#__PURE__*/_jsx("div", {
            style: {
              padding: '0 16px 8px'
            },
            children: /*#__PURE__*/_jsx(Text, {
              type: "danger",
              children: localError
            })
          }), retryAvailable && /*#__PURE__*/_jsx("div", {
            style: {
              padding: '0 16px 8px'
            },
            children: /*#__PURE__*/_jsx(Button, {
              size: "small",
              onClick: function onClick() {
                var _promptInputRef$curre2;

                return (_promptInputRef$curre2 = promptInputRef.current) === null || _promptInputRef$curre2 === void 0 ? void 0 : _promptInputRef$curre2.retryLastMessage();
              },
              children: "Retry"
            })
          }), /*#__PURE__*/_jsx(MentionListContainer, {
            mentions: mentions,
            onRemoveMention: onRemoveMention,
            onClick: onMentionClick
          }), /*#__PURE__*/_jsx(PromptInputContainer, {
            ref: promptInputRef,
            conversationId: currentConversationId,
            streamCallbacks: streamCallbacks,
            isStreaming: isStreaming,
            disabled: isLoadingGetConversationsWithMessages,
            placeholder: "Type a message... (Enter to send, Shift+Enter for new line)",
            onBuildingDataBeforeSendMessage: onBuildingDataBeforeSendMessage,
            onStopRequested: handleUserStopRequest,
            onOpenUploadModal: handleOpenUploadModal,
            onSnapshotMentionResources: onSnapshotMentionResources,
            clearOnSubmit: true,
            enableAutoResize: true,
            onStopConfirmed: handleStopConfirm,
            onStopCompleted: handleStopConfirm,
            onBeforeSend: handleBeforeSendMessage,
            walletPercent: walletPercent,
            walletLoading: walletLoading,
            onAfterStreamEnd: onAfterStreamEnd,
            extraOptions: extraOptions,
            extraFiles: extraFiles,
            userPlanId: accountingSubscription === null || accountingSubscription === void 0 ? void 0 : accountingSubscription.planId,
            onModelChange: setActiveModelRules
          })]
        })
      })
    }), fileSelectorOpen && /*#__PURE__*/_jsx(FileSelector, {
      open: fileSelectorOpen,
      onCancel: function onCancel() {
        return setFileSelectorOpen(false);
      },
      onSave: handleSave,
      isSaving: isSaving,
      allowedFileTypes: ((_activeModelRules$all = activeModelRules === null || activeModelRules === void 0 ? void 0 : activeModelRules.allowedFileExtensions) !== null && _activeModelRules$all !== void 0 ? _activeModelRules$all : []).map(function (extension) {
        return ".".concat(extension);
      }),
      fileSizeLimitMb: activeModelRules === null || activeModelRules === void 0 ? void 0 : activeModelRules.fileSizeLimit,
      totalSizeLimitMb: activeModelRules === null || activeModelRules === void 0 ? void 0 : activeModelRules.totalSizeLimit,
      defaultTab: fileSelectorTab
    }), slots === null || slots === void 0 ? void 0 : slots.mentionContext, slots === null || slots === void 0 ? void 0 : slots.changedResources, isOpenModal && /*#__PURE__*/_jsx(SuggestionItemModal, {
      item: itemSelected,
      onCancel: handleCloseModal,
      onSubmit: handleSubmitModal
    })]
  });
};